import { S3_BASE_URL, STATIC_URL } from 'constants/static';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { shopState } from 'store/atoms';
import { useEffect, useState } from 'react';
import { getEventList } from 'apis/event';
import { EventListItem } from 'types/api';
import { formatEventDateTime } from 'utils/eventDateFormat';
import { getEventStatus, EventStatus } from 'utils/getEventStatus';
import { useTranslation } from 'i18n';
import dayjs from 'dayjs';
import {
  Section,
  Title,
  Cards,
  Card,
  Media,
  EventImage,
  PlaceholderLogo,
  Content,
  Meta,
  Status,
  EventTitle,
  DateText,
  ViewAllButton,
  EmptyStateContainer,
  EmptyStateText,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalImage,
  ModalEventImage,
  ModalInfo,
  ModalEventTitle,
  ModalDateText,
  ModalFooter,
  ViewDetailsButton,
} from './EventMainPage.styles';

const EVENT_STATUS_KEY: Record<EventStatus, string> = {
  'Coming Soon': 'event.status.coming_soon',
  Ongoing: 'event.status.ongoing',
  Closed: 'event.status.closed',
};

export function EventMainPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const shop = useRecoilValue(shopState);
  const [items, setItems] = useState<EventListItem[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = (event: EventListItem) => {
    navigate(`/event/${event.idx}`);
  };

  const handleCloseModal = () => {    
    const today = dayjs().format('YYYY-MM-DD');
    localStorage.setItem('popup_event_date', today);

    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleViewDetails = () => {
    if (selectedEvent) {
      navigate(`/event/${selectedEvent.idx}`);
    }
  };

  const getEventListData = async (): Promise<EventListItem[]> => {
    const response = await getEventList({ shopSeqNo: shop?.shopSeqNo || 0 });
    const data = response.data?.results || [];

    const sorted = data
      .sort((a, b) => new Date(a.event_start).getTime() - new Date(b.event_start).getTime())
      .filter((event) => getEventStatus(event.event_start, event.event_end) !== 'Closed')
      .slice(0, 2);

    return sorted;
  };

  const shouldShowPopup = (): boolean => {
    const savedDate = localStorage.getItem('popup_event_date');
    const today = dayjs().format('YYYY-MM-DD');

    if (savedDate && savedDate !== today) {
      localStorage.removeItem('popup_event_date');
    }

    return savedDate !== today;
  };

  const findTodayEvent = (events: EventListItem[]): EventListItem | undefined => {
    return events.find((event) => {
      const start = dayjs(event.event_start);
      const end = dayjs(event.event_end);

      const isSameDayEvent = start.isSame(end, 'day');

      const isToday = dayjs().isSame(start, 'day');

      return isSameDayEvent && isToday;
    });
  };

  useEffect(() => {
    const initializeEvents = async () => {
      const eventList = await getEventListData();
      setItems(eventList);

      if (!shouldShowPopup()) {
        return;
      }

      const todayEvent = findTodayEvent(eventList);

      if (todayEvent) {
        setSelectedEvent(todayEvent);
        setIsModalOpen(true);
      }
    };

    initializeEvents();
  }, []);

  return (
    <>
      <Section>
        <Title>{t('event.title')}</Title>

        {items.length > 0 ? (
          <>
            <Cards>
              {items.map((event) => (
                <Card
                  key={event.idx}
                  type="button"
                  onClick={() => handleCardClick(event)}
                  aria-label={`event-${event.idx}`}
                >
                  <Media>
                    {event.event_thumbnail ? (
                      <EventImage src={`${STATIC_URL}${event.event_thumbnail}`} alt={event.title} />
                    ) : (
                      <PlaceholderLogo src={`${S3_BASE_URL}/kaddie_new_footer_logo.svg`} alt="kaddie logo" />
                    )}
                  </Media>
                  <Content>
                    <Meta>
                      <Status>{t(EVENT_STATUS_KEY[getEventStatus(event.event_start, event.event_end)])}</Status>
                      <EventTitle>{event.title}</EventTitle>
                    </Meta>
                    <DateText>{formatEventDateTime(event.event_start, event.event_end)}</DateText>
                  </Content>
                </Card>
              ))}
            </Cards>

            <ViewAllButton type="button" onClick={() => navigate('/event')}>
              {t('event.cta.view_all')}
            </ViewAllButton>
          </>
        ) : (
          <EmptyStateContainer>
            <EmptyStateText>{t('event.no_data')}</EmptyStateText>
          </EmptyStateContainer>
        )}
      </Section>

      {isModalOpen && selectedEvent && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <ModalHeader>
              <div style={{ width: '24px' }} />
              <ModalTitle>{t('event.popup.title')}</ModalTitle>
              <CloseButton type="button" onClick={handleCloseModal} aria-label="Close modal" />
            </ModalHeader>

            <ModalBody>
              <ModalImage>
                {selectedEvent.event_thumbnail ? (
                  <ModalEventImage src={`${STATIC_URL}${selectedEvent.event_thumbnail}`} alt={selectedEvent.title} />
                ) : (
                  <PlaceholderLogo src={`${S3_BASE_URL}/kaddie_new_footer_logo.svg`} alt="kaddie logo" />
                )}
              </ModalImage>

              <ModalInfo>
                <ModalEventTitle>{selectedEvent.title}</ModalEventTitle>
                <ModalDateText>{formatEventDateTime(selectedEvent.event_start, selectedEvent.event_end)}</ModalDateText>
              </ModalInfo>
            </ModalBody>

            <ModalFooter>
              <ViewDetailsButton type="button" onClick={handleViewDetails}>
                {t('event.popup.view_details')}
              </ViewDetailsButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}

